import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/react';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import type { AdminLeadInventoryRow } from './api/accountApi';
import {
  adminAssignLead,
  adminFetchLeadsPage,
  adminPatchLead,
  adminUnassignLead
} from './api/accountApi';
import { ACCOUNT_TABLE_CHECKBOX_CLASS } from './accountStyles';
import { propertyTypeCell } from './propertyTypeDisplay';

const AccountElLeadsPage = () => {
  const { getToken } = useAuth();
  const { notify } = useToast();
  const getTok = useCallback(() => getToken(), [getToken]);

  const [leadRows, setLeadRows] = useState<AdminLeadInventoryRow[]>([]);
  const [leadStatus, setLeadStatus] = useState<
    'all' | 'assigned' | 'unassigned' | 'fresh' | 'clearance'
  >('all');
  const [leadMissing, setLeadMissing] = useState<
    'none' | 'fsa' | 'name' | 'email' | 'phone'
  >('none');
  const [leadFlagged, setLeadFlagged] = useState<'all' | 'flagged'>('all');
  const [leadSearch, setLeadSearch] = useState('');
  const [leadSearchDebounced, setLeadSearchDebounced] = useState('');
  const [leadPageSize, setLeadPageSize] = useState<25 | 50>(25);
  const [leadCursor, setLeadCursor] = useState<string | null>(null);
  const [leadNextCursor, setLeadNextCursor] = useState<string | null>(null);
  const [leadCursorHistory, setLeadCursorHistory] = useState<
    Array<string | null>
  >([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [leadFsaDrafts, setLeadFsaDrafts] = useState<Record<string, string>>(
    {}
  );
  const [assignUserDrafts, setAssignUserDrafts] = useState<
    Record<string, string>
  >({});
  const [leadSavingId, setLeadSavingId] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setLeadSearchDebounced(leadSearch), 300);
    return () => window.clearTimeout(t);
  }, [leadSearch]);

  const loadLeads = useCallback(
    async (cursorArg: string | null = null) => {
      setLoadingLeads(true);
      try {
        const page = await adminFetchLeadsPage(getTok, {
          status: leadStatus,
          missing: leadMissing,
          flagged: leadFlagged,
          search: leadSearchDebounced,
          cursor: cursorArg,
          limit: leadPageSize
        });
        setLeadRows(page.leads);
        const fsaDrafts: Record<string, string> = {};
        const assignDrafts: Record<string, string> = {};
        for (const lead of page.leads) {
          fsaDrafts[lead.leadId] = lead.propertyFsa ?? '';
          assignDrafts[lead.leadId] = '';
        }
        setLeadFsaDrafts(fsaDrafts);
        setAssignUserDrafts(assignDrafts);
        setLeadCursor(cursorArg);
        setLeadNextCursor(page.nextCursor);
        setLeadTotal(page.total);
      } catch (e) {
        notify(
          'error',
          e instanceof Error ? e.message : 'Could not load leads'
        );
        setLeadRows([]);
        setLeadCursor(null);
        setLeadNextCursor(null);
        setLeadTotal(0);
      } finally {
        setLoadingLeads(false);
      }
    },
    [
      getTok,
      leadFlagged,
      leadMissing,
      leadPageSize,
      leadSearchDebounced,
      leadStatus,
      notify
    ]
  );

  useEffect(() => {
    setLeadCursor(null);
    setLeadNextCursor(null);
    setLeadCursorHistory([]);
    void loadLeads(null);
  }, [
    leadStatus,
    leadMissing,
    leadFlagged,
    leadPageSize,
    leadSearchDebounced,
    loadLeads
  ]);

  const saveLeadFsa = async (leadId: string) => {
    const nextFsa = (leadFsaDrafts[leadId] ?? '').trim().toUpperCase();
    setLeadSavingId(leadId);
    try {
      await adminPatchLead(getTok, leadId, { propertyFsa: nextFsa || null });
      setLeadRows(prev =>
        prev.map(r =>
          r.leadId === leadId ? { ...r, propertyFsa: nextFsa || null } : r
        )
      );
      notify('success', `Updated FSA for ${leadId}`);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Could not update FSA');
    } finally {
      setLeadSavingId(null);
    }
  };

  const toggleLeadRouting = async (leadId: string, disabled: boolean) => {
    setLeadSavingId(leadId);
    try {
      await adminPatchLead(getTok, leadId, { routingDisabled: disabled });
      setLeadRows(prev =>
        prev.map(r =>
          r.leadId === leadId
            ? {
                ...r,
                routingDisabled: disabled,
                bucket: disabled ? 'other' : r.bucket
              }
            : r
        )
      );
      notify(
        'success',
        disabled
          ? 'Lead hidden from auto-routing.'
          : 'Lead restored to routing.'
      );
      void loadLeads(leadCursor);
    } catch (e) {
      notify('error', e instanceof Error ? e.message : 'Could not update lead');
    } finally {
      setLeadSavingId(null);
    }
  };

  const assignLeadToUser = async (leadId: string) => {
    const targetUser = (assignUserDrafts[leadId] ?? '').trim();
    if (!targetUser) {
      notify(
        'error',
        'Enter a target user name, email, or Clerk user ID first.'
      );
      return;
    }
    setLeadSavingId(leadId);
    try {
      await adminAssignLead(getTok, leadId, targetUser);
      notify('success', 'Lead assigned.');
      void loadLeads(leadCursor);
    } catch (e) {
      notify(
        'error',
        e instanceof Error ? e.message : 'Manual assignment failed'
      );
    } finally {
      setLeadSavingId(null);
    }
  };

  const unassignLead = async (leadId: string) => {
    setLeadSavingId(leadId);
    try {
      await adminUnassignLead(getTok, leadId);
      notify('success', 'Lead unassigned.');
      void loadLeads(leadCursor);
    } catch (e) {
      notify(
        'error',
        e instanceof Error ? e.message : 'Could not unassign lead'
      );
    } finally {
      setLeadSavingId(null);
    }
  };

  const renderElPaginationControls = () => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-600">Per page</span>
        <select
          value={leadPageSize}
          onChange={e => setLeadPageSize(Number(e.target.value) as 25 | 50)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            const prev =
              leadCursorHistory[leadCursorHistory.length - 1] ?? null;
            setLeadCursorHistory(h => h.slice(0, -1));
            void loadLeads(prev);
          }}
          disabled={loadingLeads || leadCursorHistory.length === 0}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-xs text-slate-500">
          Showing up to {leadRows.length} of {leadTotal}
        </span>
        <button
          type="button"
          onClick={() => {
            if (!leadNextCursor) return;
            setLeadCursorHistory(h => [...h, leadCursor]);
            void loadLeads(leadNextCursor);
          }}
          disabled={loadingLeads || !leadNextCursor}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">EL Leads</h1>
        <p className="mt-1 text-sm text-slate-600">
          All inventory with full PII for EL admins.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">{leadTotal} total</div>
          <div className="flex items-center gap-2">
            <input
              value={leadSearch}
              onChange={e => setLeadSearch(e.target.value)}
              placeholder="Search leads..."
              className="w-44 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
            />
            <select
              value={leadStatus}
              onChange={e =>
                setLeadStatus(
                  e.target.value as
                    | 'all'
                    | 'assigned'
                    | 'unassigned'
                    | 'fresh'
                    | 'clearance'
                )
              }
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
            >
              <option value="all">All</option>
              <option value="fresh">Fresh</option>
              <option value="clearance">Clearance</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>
            <select
              value={leadMissing}
              onChange={e =>
                setLeadMissing(
                  e.target.value as 'none' | 'fsa' | 'name' | 'email' | 'phone'
                )
              }
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
            >
              <option value="none">Missing: none</option>
              <option value="fsa">Missing FSA</option>
              <option value="name">Missing name</option>
              <option value="email">Missing email</option>
              <option value="phone">Missing phone</option>
            </select>
            <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={leadFlagged === 'flagged'}
                onChange={e =>
                  setLeadFlagged(e.target.checked ? 'flagged' : 'all')
                }
                className={ACCOUNT_TABLE_CHECKBOX_CLASS}
              />
              Show flagged only
            </label>
            <button
              type="button"
              onClick={() => void loadLeads(leadCursor)}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>
        {loadingLeads ? (
          <div className="flex justify-center py-10">
            <Loader2
              className="h-6 w-6 animate-spin text-slate-500"
              aria-hidden
            />
          </div>
        ) : (
          <>
            <div className="mb-3">{renderElPaginationControls()}</div>
            <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Lead type</th>
                    <th className="px-3 py-2">Contact information</th>
                    <th className="px-3 py-2">Property of interest</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leadRows.map(r => (
                    <tr
                      key={r.leadId}
                      className="border-t border-slate-100 align-top"
                    >
                      <td className="px-3 py-3 tabular-nums text-slate-500">
                        {(r.leadCreatedAtIso ?? r.submittedAt)
                          ? new Date((r.leadCreatedAtIso ?? r.submittedAt)!)
                              .toISOString()
                              .slice(0, 10)
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-slate-700">
                        <div className="font-medium capitalize">
                          {r.leadType}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {r.routingDisabled ? 'other' : r.bucket} · {r.status}
                        </div>
                        {r.isFlagged ? (
                          <div className="mt-1 space-y-1">
                            <span className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                              Flagged
                            </span>
                            {r.flagReason ? (
                              <p className="text-[11px] leading-relaxed text-amber-700">
                                {r.flagReason}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-slate-700">
                        <div>{r.visitorName || '—'}</div>
                        <div className="mt-1 text-slate-600">
                          {r.visitorEmail || '—'}
                        </div>
                        <div className="mt-1 text-slate-600">
                          {r.visitorPhone || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-700">
                        <div>MLS: {r.mlsNumber ?? r.listingId ?? r.leadId}</div>
                        <div className="mt-1">
                          Property type: {propertyTypeCell(r.propertyType)}
                        </div>
                        <div className="mt-1 text-slate-600">
                          {r.propertyAddress || '—'}
                        </div>
                        <div className="mt-1">
                          Price: {r.propertyPrice ? `$${r.propertyPrice}` : '—'}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="grid gap-2 sm:min-w-[280px] sm:grid-cols-[140px_1fr]">
                          <input
                            value={leadFsaDrafts[r.leadId] ?? ''}
                            onChange={e =>
                              setLeadFsaDrafts(prev => ({
                                ...prev,
                                [r.leadId]: e.target.value
                              }))
                            }
                            placeholder="FSA"
                            className="rounded-md border border-slate-200 px-2 py-1.5 text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => void saveLeadFsa(r.leadId)}
                            disabled={leadSavingId === r.leadId}
                            className="rounded-md border border-slate-200 px-2 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          >
                            Save FSA
                          </button>
                          <input
                            value={assignUserDrafts[r.leadId] ?? ''}
                            onChange={e =>
                              setAssignUserDrafts(prev => ({
                                ...prev,
                                [r.leadId]: e.target.value
                              }))
                            }
                            placeholder="Assign by name, email, or Clerk user ID"
                            className="rounded-md border border-slate-200 px-2 py-1.5 text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => void assignLeadToUser(r.leadId)}
                            disabled={leadSavingId === r.leadId}
                            className="rounded-md border border-slate-200 px-2 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          >
                            Assign
                          </button>
                          {r.status === 'assigned' ? (
                            <button
                              type="button"
                              onClick={() => void unassignLead(r.leadId)}
                              disabled={leadSavingId === r.leadId}
                              className="sm:col-span-2 rounded-md border border-rose-200 px-2 py-1.5 font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            >
                              Unassign
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() =>
                              void toggleLeadRouting(
                                r.leadId,
                                !r.routingDisabled
                              )
                            }
                            disabled={leadSavingId === r.leadId}
                            className={`sm:col-span-2 rounded-md border px-2 py-1.5 font-medium disabled:opacity-50 ${
                              r.routingDisabled
                                ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                : 'border-amber-300 bg-amber-50 text-rose-700 hover:bg-amber-100'
                            }`}
                          >
                            {r.routingDisabled
                              ? 'Show in routing'
                              : 'Hide from routing'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3">{renderElPaginationControls()}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountElLeadsPage;
